import { create } from 'zustand';
import { toast } from 'sonner';
import {
    publishingService,
    ScheduledPost,
    Campaign,
    PlatformConnection,
    PublishingStats,
} from '../services/publishing.service';

interface PublishingFilters {
    campaignId: string;
    platformConnectionId: string;
    status: string;
}

interface PublishingState {
    // Data
    posts: ScheduledPost[];
    campaigns: Campaign[];
    platforms: PlatformConnection[];
    stats: PublishingStats | null;
    selectedPost: ScheduledPost | null;
    selectedCampaign: Campaign | null;

    // UI State
    isLoading: boolean;
    activeTab: 'queue' | 'campaigns' | 'platforms';
    filters: PublishingFilters;
    searchTerm: string;

    // Actions - Posts
    fetchPosts: () => Promise<void>;
    fetchPost: (id: string) => Promise<void>;
    createPost: (data: Parameters<typeof publishingService.createPost>[0]) => Promise<ScheduledPost>;
    updatePost: (id: string, data: Partial<ScheduledPost>) => Promise<void>;
    deletePost: (id: string) => Promise<void>;
    publishNow: (id: string) => Promise<void>;
    setSelectedPost: (post: ScheduledPost | null) => void;

    // Actions - Campaigns
    fetchCampaigns: () => Promise<void>;
    fetchCampaign: (id: string) => Promise<void>;
    createCampaign: (data: Parameters<typeof publishingService.createCampaign>[0]) => Promise<Campaign>;
    updateCampaign: (id: string, data: Partial<Campaign>) => Promise<void>;
    deleteCampaign: (id: string) => Promise<void>;
    setSelectedCampaign: (campaign: Campaign | null) => void;

    // Actions - Platforms
    fetchPlatforms: () => Promise<void>;
    connectPlatform: (data: Parameters<typeof publishingService.connectPlatform>[0]) => Promise<PlatformConnection>;
    refreshPlatform: (id: string) => Promise<void>;
    disconnectPlatform: (id: string) => Promise<void>;

    // Actions - Stats
    fetchStats: () => Promise<void>;

    // Actions - UI
    setActiveTab: (tab: 'queue' | 'campaigns' | 'platforms') => void;
    setFilters: (filters: Partial<PublishingFilters>) => void;
    setSearchTerm: (term: string) => void;

    // Helpers
    getFilteredPosts: () => ScheduledPost[];
}

export const usePublishingStore = create<PublishingState>((set, get) => ({
    // Initial State
    posts: [],
    campaigns: [],
    platforms: [],
    stats: null,
    selectedPost: null,
    selectedCampaign: null,
    isLoading: false,
    activeTab: 'queue',
    filters: {
        campaignId: '',
        platformConnectionId: '',
        status: '',
    },
    searchTerm: '',

    // Posts Actions
    fetchPosts: async () => {
        try {
            set({ isLoading: true });
            const { filters } = get();
            const apiFilters: {
                campaignId?: string;
                platformConnectionId?: string;
                status?: string;
            } = {};

            if (filters.campaignId) {
                apiFilters.campaignId = filters.campaignId;
            }
            if (filters.platformConnectionId) {
                apiFilters.platformConnectionId = filters.platformConnectionId;
            }
            if (filters.status) {
                apiFilters.status = filters.status;
            }

            const posts = await publishingService.getPosts(apiFilters);
            set({ posts });
        } catch (error) {
            console.error('Failed to fetch posts:', error);
            toast.error('Failed to load scheduled posts');
        } finally {
            set({ isLoading: false });
        }
    },

    fetchPost: async (id: string) => {
        try {
            const post = await publishingService.getPost(id);
            set({ selectedPost: post });
        } catch (error) {
            console.error('Failed to fetch post:', error);
            toast.error('Failed to load post details');
        }
    },

    createPost: async (data) => {
        try {
            const post = await publishingService.createPost(data);
            set((state) => ({ posts: [...state.posts, post] }));
            toast.success('Post scheduled successfully');
            get().fetchStats();
            return post;
        } catch (error) {
            console.error('Failed to create post:', error);
            toast.error('Failed to schedule post');
            throw error;
        }
    },

    updatePost: async (id: string, data) => {
        try {
            const updatedPost = await publishingService.updatePost(id, data);
            set((state) => ({
                posts: state.posts.map((p) => (p.id === id ? updatedPost : p)),
                selectedPost: state.selectedPost?.id === id ? updatedPost : state.selectedPost,
            }));
            toast.success('Post updated successfully');
        } catch (error) {
            console.error('Failed to update post:', error);
            toast.error('Failed to update post');
            throw error;
        }
    },

    deletePost: async (id: string) => {
        try {
            await publishingService.deletePost(id);
            set((state) => ({
                posts: state.posts.filter((p) => p.id !== id),
                selectedPost: state.selectedPost?.id === id ? null : state.selectedPost,
            }));
            toast.success('Post deleted successfully');
            get().fetchStats();
        } catch (error) {
            console.error('Failed to delete post:', error);
            toast.error('Failed to delete post');
        }
    },

    publishNow: async (id: string) => {
        try {
            const publishedPost = await publishingService.publishNow(id);
            set((state) => ({
                posts: state.posts.map((p) => (p.id === id ? publishedPost : p)),
                selectedPost: state.selectedPost?.id === id ? publishedPost : state.selectedPost,
            }));
            toast.success('Post published successfully');
            get().fetchStats();
        } catch (error) {
            console.error('Failed to publish post:', error);
            toast.error('Failed to publish post');
        }
    },

    setSelectedPost: (post) => set({ selectedPost: post }),

    // Campaigns Actions
    fetchCampaigns: async () => {
        try {
            set({ isLoading: true });
            const campaigns = await publishingService.getCampaigns();
            set({ campaigns });
        } catch (error) {
            console.error('Failed to fetch campaigns:', error);
            toast.error('Failed to load campaigns');
        } finally {
            set({ isLoading: false });
        }
    },

    fetchCampaign: async (id: string) => {
        try {
            const campaign = await publishingService.getCampaign(id);
            set({ selectedCampaign: campaign });
        } catch (error) {
            console.error('Failed to fetch campaign:', error);
            toast.error('Failed to load campaign details');
        }
    },

    createCampaign: async (data) => {
        try {
            const campaign = await publishingService.createCampaign(data);
            set((state) => ({ campaigns: [...state.campaigns, campaign] }));
            toast.success('Campaign created successfully');
            return campaign;
        } catch (error) {
            console.error('Failed to create campaign:', error);
            toast.error('Failed to create campaign');
            throw error;
        }
    },

    updateCampaign: async (id: string, data) => {
        try {
            const updatedCampaign = await publishingService.updateCampaign(id, data);
            set((state) => ({
                campaigns: state.campaigns.map((c) => (c.id === id ? updatedCampaign : c)),
                selectedCampaign: state.selectedCampaign?.id === id ? updatedCampaign : state.selectedCampaign,
            }));
            toast.success('Campaign updated successfully');
        } catch (error) {
            console.error('Failed to update campaign:', error);
            toast.error('Failed to update campaign');
            throw error;
        }
    },

    deleteCampaign: async (id: string) => {
        try {
            await publishingService.deleteCampaign(id);
            set((state) => ({
                campaigns: state.campaigns.filter((c) => c.id !== id),
                selectedCampaign: state.selectedCampaign?.id === id ? null : state.selectedCampaign,
            }));
            toast.success('Campaign deleted successfully');
        } catch (error) {
            console.error('Failed to delete campaign:', error);
            toast.error('Failed to delete campaign');
        }
    },

    setSelectedCampaign: (campaign) => set({ selectedCampaign: campaign }),

    // Platforms Actions
    fetchPlatforms: async () => {
        try {
            set({ isLoading: true });
            const platforms = await publishingService.getPlatforms();
            set({ platforms });
        } catch (error) {
            console.error('Failed to fetch platforms:', error);
            toast.error('Failed to load platform connections');
        } finally {
            set({ isLoading: false });
        }
    },

    connectPlatform: async (data) => {
        try {
            const platform = await publishingService.connectPlatform(data);
            set((state) => ({ platforms: [...state.platforms, platform] }));
            toast.success(`${data.platform} connected successfully`);
            return platform;
        } catch (error: any) {
            console.error('Failed to connect platform:', error);
            toast.error(error.response?.data?.message || 'Failed to connect platform');
            throw error;
        }
    },

    refreshPlatform: async (id: string) => {
        try {
            const refreshedPlatform = await publishingService.refreshPlatform(id);
            set((state) => ({
                platforms: state.platforms.map((p) => (p.id === id ? refreshedPlatform : p)),
            }));
            toast.success('Platform connection refreshed');
        } catch (error) {
            console.error('Failed to refresh platform:', error);
            toast.error('Failed to refresh platform connection');
        }
    },

    disconnectPlatform: async (id: string) => {
        try {
            await publishingService.disconnectPlatform(id);
            set((state) => ({
                platforms: state.platforms.filter((p) => p.id !== id),
            }));
            toast.success('Platform disconnected successfully');
        } catch (error) {
            console.error('Failed to disconnect platform:', error);
            toast.error('Failed to disconnect platform');
        }
    },

    // Stats Actions
    fetchStats: async () => {
        try {
            const stats = await publishingService.getStats();
            set({ stats });
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    },

    // UI Actions
    setActiveTab: (tab) => set({ activeTab: tab }),

    setFilters: (filters) => set((state) => ({
        filters: { ...state.filters, ...filters },
    })),

    setSearchTerm: (term) => set({ searchTerm: term }),

    // Helpers
    getFilteredPosts: () => {
        const { posts, searchTerm, filters } = get();

        return posts.filter((post) => {
            const matchesSearch =
                !searchTerm ||
                post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                post.description?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesCampaign =
                !filters.campaignId ||
                (typeof post.campaignId === 'string'
                    ? post.campaignId === filters.campaignId
                    : (post.campaignId as Campaign)?._id === filters.campaignId);

            const matchesPlatform =
                !filters.platformConnectionId ||
                (typeof post.platformConnectionId === 'string'
                    ? post.platformConnectionId === filters.platformConnectionId
                    : (post.platformConnectionId as PlatformConnection)?._id === filters.platformConnectionId);

            const matchesStatus = !filters.status || post.status === filters.status;

            return matchesSearch && matchesCampaign && matchesPlatform && matchesStatus;
        });
    },
}));
